import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
    Check, X, Play, Clock, ShieldAlert,
    User, Info, Code,
    Search, Filter, RefreshCw, XCircle, ExternalLink,
    Lock, AlertTriangle, Zap, Fingerprint
} from 'lucide-react';
import { SnackbarContext } from '../App';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { cn } from '../lib/utils';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import StatusBadge from './ui/StatusBadge';
import EmptyState from './ui/EmptyState';
import api from '../api/api';

const ACTION_TYPES = [
    'SUPPRESSION_RELEASE',
    'WEBHOOK_SECRET_ROTATION',
    'OUTBOX_REPLAY',
    'BULK_CRM_UPDATE',
    'CRM_DATA_EXPORT'
];

const STATUS_OPTIONS = [
    'PENDING',
    'APPROVED',
    'REJECTED',
    'EXECUTING',
    'EXECUTED',
    'FAILED'
];

const OP_EXPLANATIONS = {
    'SUPPRESSION_RELEASE': {
        label: 'Release Email Suppression',
        category: 'Compliance',
        description: 'Authorized release of an email suppression record (bounce/complaint).',
        risk: 'Directly impacts sender reputation. May re-enable delivery to invalid or hostile addresses.',
        consequence: 'The selected addresses will be instantly eligible for outbound communication modules.'
    },
    'WEBHOOK_SECRET_ROTATION': {
        label: 'Rotate Webhook Secret',
        category: 'Security',
        description: 'Regeneration of the cryptographic secret used for signing outgoing webhooks.',
        risk: 'Will break existing external integrations until they are updated with the new token.',
        consequence: 'Old secret is invalidated immediately. A new cryptographically secure token is generated.'
    },
    'OUTBOX_REPLAY': {
        label: 'Re-queue Outbox Messages',
        category: 'System Health',
        description: 'Re-insertion of failed or dead-letter outbox items back into the dispatch queue.',
        risk: 'If partially sent previously, this may result in duplicate communication to the recipient.',
        consequence: 'Stalled items are reset to PENDING and will be picked up by the next worker cycle.'
    },
    'BULK_CRM_UPDATE': {
        label: 'Bulk CRM Mutation',
        category: 'Data Mutation',
        description: 'Mass-update of specific fields (stage, tags, status) across multiple contacts.',
        risk: 'High impact data change. Incorrect filters can lead to widespread CRM data corruption.',
        consequence: 'Atomic field updates applied to all target Contact IDs within the workspace.'
    },
    'CRM_DATA_EXPORT': {
        label: 'Authorize Data Export',
        category: 'Privacy',
        description: 'Review and approval of a request to export sensitive CRM data snapshots.',
        risk: 'Data exfiltration risk. Large snapshots contain PII and contact engagement history.',
        consequence: 'Unlocks the download capability for the requesting operator for a bounded period.'
    }
};

export default function Approvals() {
    const showSnackbar = useContext(SnackbarContext);
    const navigate = useNavigate();
    
    // -- Route-Backed State --
    const [searchParams, setSearchParams] = useSearchParams();
    
    // Derived values from URL
    const filterStatus = searchParams.get('status') || 'ALL';
    const filterAction = searchParams.get('actionType') || 'ALL';
    const filterMaker = searchParams.get('requestedBy') || '';
    const selectedId = searchParams.get('requestId');

    // -- Component State --
    const [approvals, setApprovals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // -- Helpers --
    const updateUrlParam = useCallback((key, value) => {
        const newParams = new URLSearchParams(searchParams);
        if (value && value !== 'ALL') {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }

        if (key !== 'requestId') {
            newParams.delete('requestId');
        }

        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    const clearFilters = () => {
        setSearchParams({});
    };

    const handleApiError = useCallback((error) => {
        const msg = error.isAppError ? `${error.code}: ${error.message}` : 'An unexpected error occurred.';
        showSnackbar(msg, 'error');
    }, [showSnackbar]);

    // -- Fetching --
    const fetchApprovals = useCallback(async (silent = false) => {
        try {
            if (!silent) setLoading(true);

            const params = new URLSearchParams();
            if (filterStatus !== 'ALL') params.set('status', filterStatus);
            if (filterAction !== 'ALL') params.set('actionType', filterAction);
            if (filterMaker) params.set('requestedBy', filterMaker);

            const res = await api.get(`/api/approvals?${params.toString()}`);
            setApprovals(res.data);

            if (res.data.length > 0 && !selectedId && !silent) {
                updateUrlParam('requestId', res.data[0]._id);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [filterStatus, filterAction, filterMaker, selectedId, updateUrlParam, handleApiError]);

    const fetchDetail = useCallback(async (id) => {
        if (!id) return;
        try {
            setDetailLoading(true);
            const res = await api.get(`/api/approvals/${id}`);
            setDetail(res.data);
        } catch (error) {
            handleApiError(error);
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    }, [handleApiError]);

    // -- Effects --
    useEffect(() => {
        fetchApprovals();
    }, [fetchApprovals]);

    useEffect(() => {
        if (selectedId) {
            fetchDetail(selectedId);
        } else {
            setDetail(null);
        }
    }, [selectedId, fetchDetail]);

    const handleAction = async (id, action) => {
        try {
            setActionLoading(true);
            if (action === 'approve' || action === 'reject') {
                await api.put(`/api/approvals/${id}/${action}`, {});
            } else {
                await api.post(`/api/approvals/${id}/${action}`, {});
            }
            
            showSnackbar(`Successfully ${action}d request`, 'success');
            
            if (action === 'approve' || action === 'reject') {
                const currentIndex = approvals.findIndex(a => a._id === id);
                const nextPending = approvals.slice(currentIndex + 1).find(a => a.status === 'PENDING');
                
                if (nextPending) {
                    updateUrlParam('requestId', nextPending._id);
                    showSnackbar('Navigated to next pending request', 'default');
                }
            }

            await fetchApprovals(true);
            await fetchDetail(id);
        } catch (error) {
            handleApiError(error);
        } finally {
            setActionLoading(false);
        }
    };

    const getSourceLink = (req) => {
        if (!req) return null;
        const payload = req.resolvedPayload || req.payloadSnapshot || {};
        
        switch (req.actionType) {
            case 'SUPPRESSION_RELEASE': return '/suppressions';
            case 'WEBHOOK_SECRET_ROTATION': return '/vault';
            case 'OUTBOX_REPLAY': return '/ops';
            case 'BULK_CRM_UPDATE': return `/crm/contacts?ids=${payload.contactIds?.join(',') || ''}`;
            case 'CRM_DATA_EXPORT': return '/crm/contacts';
            default: return null;
        }
    };

    const getInitials = (user) => {
        if (!user) return '??';
        if (user.name) {
            return user.name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
        }
        return user.email?.substring(0, 2).toUpperCase() || 'U';
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString(undefined, { 
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
        });
    };

    // -- Render Parts --
    const renderFilterBar = () => (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-card border rounded-xl shadow-sm mb-4">
            <div className="flex items-center gap-2 group">
                <span className="text-[10px] font-black uppercase text-muted-foreground ml-1">Status:</span>
                <Select value={filterStatus} onValueChange={(v) => updateUrlParam('status', v)}>
                    <SelectTrigger className="h-8 w-[140px] text-xs font-bold border-dashed hover:border-primary transition-colors">
                        <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL" className="text-xs font-bold">ALL STATUSES</SelectItem>
                        {STATUS_OPTIONS.map(s => (
                            <SelectItem key={s} value={s} className="text-xs font-bold">{s}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase text-muted-foreground">Operation:</span>
                <Select value={filterAction} onValueChange={(v) => updateUrlParam('actionType', v)}>
                    <SelectTrigger className="h-8 w-[180px] text-xs font-bold border-dashed hover:border-primary transition-colors">
                        <SelectValue placeholder="All Operations" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="ALL" className="text-xs font-bold">ALL OPERATIONS</SelectItem>
                        {ACTION_TYPES.map(a => (
                            <SelectItem key={a} value={a} className="text-xs font-bold">{OP_EXPLANATIONS[a]?.label || a}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase text-muted-foreground text-right min-w-[70px]">Maker ID:</span>
                <div className="relative">
                    <Input 
                        placeholder="Search by User ID..." 
                        value={filterMaker} 
                        onChange={(e) => updateUrlParam('requestedBy', e.target.value)}
                        className="h-8 w-[180px] text-xs font-bold border-dashed"
                    />
                    {filterMaker && (
                        <button 
                            onClick={() => updateUrlParam('requestedBy', '')}
                            className="absolute right-2 top-1.5 text-muted-foreground hover:text-foreground"
                        >
                            <XCircle className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {(filterStatus !== 'ALL' || filterAction !== 'ALL' || filterMaker) && (
                <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearFilters}
                    className="h-8 text-[10px] font-black uppercase tracking-tighter text-muted-foreground hover:text-red-500"
                >
                    Clear Filters
                </Button>
            )}
        </div>
    );

    const renderSidebar = () => {
        if (loading) {
            return Array(5).fill(0).map((_, i) => (
                <div key={i} className="p-4 border-b space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-8 w-1/4 rounded-full" />
                </div>
            ));
        }

        if (approvals.length === 0) {
            return (
                <div className="p-12 text-center text-muted-foreground">
                    <ShieldAlert className="mx-auto h-8 w-8 mb-3 opacity-20" />
                    <p className="text-[10px] uppercase tracking-widest font-black">Queue Empty</p>
                    <p className="text-[9px] mt-1">Try adjusting the filter scope.</p>
                </div>
            );
        }

        return approvals.map((req) => {
            const exp = OP_EXPLANATIONS[req.actionType];
            return (
                <div 
                    key={req._id}
                    onClick={() => updateUrlParam('requestId', req._id)}
                    className={cn(
                        "p-4 border-b cursor-pointer transition-all hover:bg-muted/50",
                        selectedId === req._id ? "bg-muted border-l-4 border-l-primary shadow-inner" : "border-l-4 border-l-transparent"
                    )}
                >
                    <div className="flex justify-between items-start mb-1">
                        <h3 className="font-bold text-sm truncate pr-2">{exp?.label || req.actionType.replace(/_/g, ' ')}</h3>
                        <StatusBadge status={req.status} />
                    </div>
                    {exp && <p className="text-[9px] font-black uppercase tracking-widest text-primary mb-2 opacity-70">{exp.category}</p>}
                    <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-tight">
                        <User className="w-3 h-3" />
                        <span className="truncate max-w-[120px]">{req.requestedByUserId?.name || req.requestedByUserId?.email || 'System'}</span>
                        <span className="ml-auto shrink-0">{new Date(req.createdAt).toLocaleDateString()}</span>
                    </div>
                </div>
            );
        });
    };

    const renderDetail = () => {
        if (!selectedId) return <EmptyState icon={Search} title="Review Needed" description="Select a governance request from the queue to inspect risk, payload, and integrity seal." />;
        
        if (detailLoading && !detail) {
            return (
                <div className="p-8 space-y-6">
                    <Skeleton className="h-10 w-1/2" />
                    <Skeleton className="h-32 w-full" />
                    <div className="grid grid-cols-2 gap-4">
                        <Skeleton className="h-24 w-full" />
                        <Skeleton className="h-24 w-full" />
                    </div>
                </div>
            );
        }

        if (!detail) return <EmptyState icon={ShieldAlert} title="Missing Data" description="Could not load details for this request. It may have been deleted or moved scope." />;

        const activeStatus = detail.status;
        const sourceLink = getSourceLink(detail);
        const explanation = OP_EXPLANATIONS[detail.actionType];

        return (
            <div className="flex flex-col h-full bg-card">
                {/* Detail Header */}
                <div className="p-6 border-b bg-muted/20">
                    <div className="flex justify-between items-start mb-4">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Badge variant="outline" className="text-[10px] font-black uppercase">{explanation?.category || 'Governance'}</Badge>
                                <span className="text-muted-foreground text-xs font-mono">{detail._id}</span>
                            </div>
                            <h2 className="text-2xl font-black tracking-tight uppercase leading-none">{explanation?.label || 'Review Operation'}</h2>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                            <StatusBadge status={activeStatus} dot={activeStatus === 'EXECUTING'} className="px-4 py-2 text-xs border-2" />
                            {sourceLink && (
                                <Button 
                                    variant="link" 
                                    size="sm" 
                                    onClick={() => navigate(sourceLink)}
                                    className="text-[10px] font-black uppercase h-auto p-0 flex items-center gap-1 opacity-70 hover:opacity-100"
                                >
                                    Review Source <ExternalLink className="w-3 h-3" />
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-4 gap-3">
                        <div className="p-3 rounded-lg bg-background border shadow-sm">
                            <p className="text-[10px] uppercase font-black text-muted-foreground mb-1">Requested By</p>
                            <div className="flex items-center gap-2">
                                <Avatar className="h-6 w-6">
                                    <AvatarFallback className="text-[10px] font-black">{getInitials(detail.requestedByUserId)}</AvatarFallback>
                                </Avatar>
                                <span className="text-sm font-semibold truncate">{detail.requestedByUserId?.name || 'User'}</span>
                            </div>
                        </div>
                        <div className="p-3 rounded-lg bg-background border shadow-sm">
                            <p className="text-[10px] uppercase font-black text-muted-foreground mb-1">Expires In</p>
                            <p className="text-sm font-semibold flex items-center gap-1.5">
                                <Clock className="w-3.5 h-3.5 text-orange-500" />
                                {Math.max(0, Math.ceil((new Date(detail.expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))} Days
                            </p>
                        </div>
                        <div className="p-3 rounded-lg bg-background border shadow-sm col-span-2">
                            <p className="text-[10px] uppercase font-black text-muted-foreground mb-1">Integrity Seal (SHA-256)</p>
                            <p className="text-sm font-mono flex items-center gap-1.5 truncate">
                                <Fingerprint className="w-3.5 h-3.5 text-blue-500" />
                                <span className="opacity-70">{detail.payloadHash}</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Detail Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Operation Insight (Explainability Card) */}
                    <Card className="border-l-4 border-l-primary bg-muted/5">
                        <CardHeader className="py-4">
                            <CardTitle className="text-xs font-black uppercase tracking-widest flex items-center gap-2 text-primary">
                                <Zap className="w-3.5 h-3.5" /> Operation Insight
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 text-sm pb-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <p className="text-[10px] font-black uppercase text-muted-foreground flex items-center gap-1.5">
                                        <Info className="w-3 h-3" /> Core Purpose
                                    </p>
                                    <p className="font-medium text-foreground/90">{explanation?.description || 'No detailed description available for this action type.'}</p>
                                </div>
                                <div className="space-y-2">
                                    <p className="text-[10px] font-black uppercase text-red-500 flex items-center gap-1.5">
                                        <AlertTriangle className="w-3 h-3" /> Oversight Requirement (Risk)
                                    </p>
                                    <p className="font-medium text-foreground/90 leading-snug">{explanation?.risk || 'Requires secondary authorization due to high-risk data/system implications.'}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Governance Metadata */}
                    <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <h4 className="text-[10px] uppercase font-black tracking-widest text-muted-foreground flex items-center gap-1.5">
                                <Info className="w-3 h-3" /> Operator Note
                            </h4>
                            <div className="p-4 rounded-xl bg-muted/10 border border-dashed text-sm italic">
                                {detail.note || "No additional context provided by the operator."}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <h4 className="text-[10px] uppercase font-black tracking-widest text-muted-foreground flex items-center gap-1.5">
                                <Lock className="w-3 h-3" /> Integrity Disclosure
                            </h4>
                            <div className="p-4 rounded-xl bg-muted/10 border border-dashed text-[11px] leading-relaxed">
                                This request is **tamper-evident**. The payload hash above represents a deterministic snapshot taken at **{new Date(detail.createdAt).toLocaleTimeString()}**. 
                                The execution engine will re-verify this hash *immediately before* processing any side effects.
                            </div>
                        </div>
                    </div>

                    {/* Payload Preview */}
                    <div className="space-y-2">
                        <h4 className="text-[10px] uppercase font-black tracking-widest text-muted-foreground flex items-center gap-1.5">
                            <Code className="w-3 h-3" /> Payload Execution Snapshot
                        </h4>
                        <div className="rounded-xl border bg-zinc-950 p-4 font-mono text-[11px] overflow-x-auto text-zinc-300">
                            <pre>{JSON.stringify(detail.resolvedPayload || detail.payloadSnapshot, null, 2)}</pre>
                        </div>
                        <p className="text-[9px] text-muted-foreground uppercase text-right">
                            Payload resolution: {detail.payloadId ? 'OFFLOADED CHUNK' : 'INLINE SNAPSHOT'}
                        </p>
                    </div>

                    {/* Timeline / Actors */}
                    <div className="grid grid-cols-2 gap-6 pt-4 border-t">
                        <div className="space-y-3">
                            <h4 className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Governance History</h4>
                            {detail.approvedByUserId && (
                                <div className="flex items-start gap-3">
                                    <Avatar className="h-8 w-8 ring-2 ring-emerald-500/20">
                                        <AvatarFallback className="bg-emerald-500/10 text-emerald-600 text-xs font-black">{getInitials(detail.approvedByUserId)}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <p className="text-xs font-black tracking-tighter">APPROVED BY</p>
                                        <p className="text-sm font-bold truncate max-w-[120px]">{detail.approvedByUserId.name}</p>
                                        <p className="text-[10px] text-muted-foreground">{formatDate(detail.approvedAt)}</p>
                                    </div>
                                </div>
                            )}
                            {!detail.approvedByUserId && detail.status === 'PENDING' && (
                                <p className="text-xs text-muted-foreground italic">Awaiting checker authorization...</p>
                            )}
                            {detail.status === 'REJECTED' && (
                                <div className="flex items-start gap-3">
                                    <Avatar className="h-8 w-8 ring-2 ring-red-500/20">
                                        <AvatarFallback className="bg-red-500/10 text-red-600 text-xs font-black">RX</AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <p className="text-xs font-black tracking-tighter text-red-500">REJECTED</p>
                                        <p className="text-sm font-bold text-red-600">{detail.rejectionReason || 'No reason provided'}</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="space-y-3">
                            <h4 className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Execution History</h4>
                            {detail.executedByUserId && (
                                <div className="flex items-start gap-3">
                                    <Avatar className="h-8 w-8 ring-2 ring-indigo-500/20">
                                        <AvatarFallback className="bg-indigo-500/10 text-indigo-600 text-xs font-black">{getInitials(detail.executedByUserId)}</AvatarFallback>
                                    </Avatar>
                                    <div>
                                        <p className="text-xs font-black tracking-tighter">EXECUTED BY</p>
                                        <p className="text-sm font-bold truncate max-w-[120px]">{detail.executedByUserId.name}</p>
                                        <p className="text-[10px] text-muted-foreground">{formatDate(detail.executedAt)}</p>
                                    </div>
                                </div>
                            )}
                            {!detail.executedByUserId && detail.status === 'APPROVED' && (
                                <p className="text-xs text-muted-foreground italic">Authorized. Awaiting manual execution...</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Detail Actions */}
                <div className="p-6 border-t bg-muted/10 flex items-center justify-between">
                    <div className="flex-1 mr-6">
                        {activeStatus === 'PENDING' && (
                            <p className="text-[11px] text-muted-foreground font-medium flex items-start gap-1.5 leading-tight">
                                <ShieldAlert className="w-3.5 h-3.5 shrink-0 text-primary mt-0.5" />
                                Review the payload and original context. If authorized, this request will transition to APPROVED and become executable by any authorized operator.
                            </p>
                        )}
                        {activeStatus === 'APPROVED' && (
                            <div className="space-y-1">
                                <p className="text-[11px] text-indigo-600 font-black uppercase flex items-center gap-1.5 leading-tight tracking-tighter">
                                    <Zap className="w-3.5 h-3.5 shrink-0" /> Execution Consequence
                                </p>
                                <p className="text-[10px] text-muted-foreground font-medium leading-tight">
                                    {explanation?.consequence || 'Executing this request will trigger a live side-effect based on the identified payload snapshot.'}
                                </p>
                            </div>
                        )}
                        {(activeStatus === 'EXECUTED' || activeStatus === 'FAILED' || activeStatus === 'REJECTED') && (
                            <div className="flex items-center gap-2 text-muted-foreground text-xs font-black uppercase tracking-widest opacity-50">
                                Lifecycle Closed <Fingerprint className="w-3 h-3" />
                            </div>
                        )}
                    </div>

                    <div className="flex gap-3 shrink-0">
                        {activeStatus === 'PENDING' && (
                            <>
                                <Button 
                                    variant="outline" 
                                    className="border-red-500/20 text-red-500 hover:bg-red-500/10 h-10 px-6 font-bold"
                                    onClick={() => handleAction(detail._id, 'reject')}
                                    disabled={actionLoading}
                                >
                                    <X className="w-4 h-4 mr-2" /> Reject
                                </Button>
                                <Button 
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-8 font-black"
                                    onClick={() => handleAction(detail._id, 'approve')}
                                    disabled={actionLoading}
                                >
                                    <Check className="w-4 h-4 mr-2" /> Approve
                                </Button>
                            </>
                        )}
                        {activeStatus === 'APPROVED' && (
                            <Button 
                                className="bg-indigo-600 hover:bg-indigo-700 text-white px-10 h-10 font-black shadow-lg shadow-indigo-500/20"
                                onClick={() => handleAction(detail._id, 'execute')}
                                disabled={actionLoading}
                            >
                                {actionLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />} 
                                Execute Action
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <PageShell className="flex flex-col h-screen overflow-hidden">
            <PageHeader
                title="Security & Governance"
                subtitle="Explainable Maker/Checker review for high-risk operations."
                icon={Fingerprint}
                actions={
                    <Button onClick={() => fetchApprovals()} variant="outline" size="sm" className="h-8">
                        <RefreshCw className={cn("w-3.5 h-3.5 mr-2", loading && "animate-spin")} />
                        Refresh Queue
                    </Button>
                }
            />

            <div className="flex-1 mt-6 border rounded-2xl overflow-hidden shadow-2xl bg-muted/10 min-h-0 flex flex-col">
                {/* Filters */}
                {renderFilterBar()}

                <div className="flex-1 flex min-h-0 divide-x bg-card/30">
                    {/* Master: Sidebar Queue */}
                    <div className="w-[350px] flex flex-col bg-card/50">
                        <div className="p-3 border-b bg-muted/20 flex items-center justify-between">
                            <span className="text-[10px] font-black uppercase tracking-tighter text-muted-foreground flex items-center gap-1.5">
                                <Filter className="w-3 h-3" /> Queue Density: {approvals.length}
                            </span>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {renderSidebar()}
                        </div>
                    </div>

                    {/* Detail: Inspector Pane */}
                    <div className="flex-1 overflow-hidden min-w-0">
                        {renderDetail()}
                    </div>
                </div>
            </div>
        </PageShell>
    );
}
